import {createStore} from '@lib/create-store';
import {enableMapSet, castDraft} from 'immer';

import {MissingStoreDependencyError} from '../errors';
import {getStorage} from '../storage';

import {poll} from './helpers';
import {isDownloadUrlResult} from './schema';
import {interruptedTasksSelector, pendingTasksSelector} from './selectors';
import type {BackgroundTasksStore, Dependencies, Task} from './types';

// Adjust the import path accordingly

enableMapSet();

const storage = getStorage<BackgroundTasksStore>();
const NAME = `linx.BackgroundTasks`;

/**
 * Creates a store for managing background tasks with dependencies for subscribing to task updates.
 *
 * @param {Dependencies} dependencies - The dependencies required for subscribing to task updates.
 * @returns {StoreApi<BackgroundTasksStore>} - The Zustand store API for background tasks.
 * @throws {MissingStoreDependencyError} - If required dependencies are missing.
 */
export const createBackgroundTasksStore = (dependencies?: Dependencies) => {
  const {subscribe} = dependencies || {};

  if (!subscribe) {
    throw new MissingStoreDependencyError(NAME, `subscribe`);
  }

  const subscribeForTask = (task: Task) => (taskId: string) => subscribe(taskId, task.subscribeTarget);

  const store = createStore<BackgroundTasksStore>(
    (set, get) => ({
      // State
      tasks: new Map(),
      // External actions
      updateTask: (taskId: string, task) => {
        set(draft => {
          const state = castDraft(get().tasks.get(taskId));
          const writeableTask = castDraft(task);
          draft.tasks.set(taskId, {
            ...state,
            ...writeableTask,
          });
        });
      },
      addTask: (taskId: string, task) => {
        set(draft => {
          const state = castDraft(get().tasks.get(taskId));
          const next = castDraft(task);
          draft.tasks.set(taskId, {
            ...state,
            ...next,
          });
        });
        return task;
      },
      removeTask: id => {
        set(draft => {
          draft.tasks.delete(id);
        });
      },
      // Internal actions
      _onTaskPending: (taskId: string, task) => {
        set(draft => {
          const state = castDraft(get().tasks.get(taskId));
          const next = castDraft(task);
          draft.tasks.set(taskId, {
            ...state,
            type: `PENDING`,
            ...next,
            isFetching: true,
          });
        });
      },
      _onTaskFailure: (taskId, payload) => {
        set(draft => {
          const state = castDraft(get().tasks.get(taskId));
          const next = castDraft(payload);
          draft.tasks.set(taskId, {
            ...state,
            ...next,
            type: `FAILURE`,
            isFetching: false,
            isNotified: false,
          });
        });
      },
      _onTaskSuccess: (taskId, payload) => {
        if (isDownloadUrlResult(payload).success) {
          const link = document.createElement(`a`);
          link.href = payload?.payload.url;
          link.target = `_parent`;
          link.download = taskId;
          link.click();
          link.remove();
        }

        set(draft => {
          const state = castDraft(get().tasks.get(taskId));
          const next = castDraft(payload);
          draft.tasks.set(taskId, {
            ...state,
            ...next,
            type: 'SUCCESS',
            isFetching: false,
            isNotified: false,
          });
        });
      },
    }),
    {
      name: NAME,
      version: 1,
      devtools: true,
      persist: {
        storage,
        version: 1,
      },
    },
  );

  store.persist.onFinishHydration(() => {
    store.setState(draft => {
      draft.tasks.forEach((task, id) => {
        if (task.type !== 'PENDING' || !task.isFetching) draft.tasks.delete(id);
      });
    });
  });

  // Fetch the tasks that are pending, fetching, and don't have a payload (Probably interrupted tasks)
  const interruptedTasks = interruptedTasksSelector(store.getState());

  interruptedTasks.forEach(([taskId, task]) => {
    store.getState()._onTaskPending(taskId);

    return poll(subscribeForTask(task), taskId, {
      onSuccess: result => {
        store.getState()._onTaskSuccess(taskId, result);
      },
      onError: result => {
        store.getState()._onTaskFailure(taskId, result);
      },
      interval: task.interval,
    });
  });

  store.subscribe((state, prevState) => {
    const {_onTaskFailure, _onTaskPending, _onTaskSuccess} = state;

    const previousTasks = Array.from(pendingTasksSelector(prevState).entries()).filter(
      ([_id, task]) => !task.isFetching,
    );
    const currentTasks = Array.from(pendingTasksSelector(state).entries()).filter(([_id, task]) => !task.isFetching);

    // Find the difference between the previous tasks and the current tasks
    const newTasks = currentTasks.filter(([id]) => !previousTasks.some(([prevId]) => prevId === id));

    // For each of those tasks, subscribe to them
    newTasks.forEach(([taskId, task]) => {
      _onTaskPending(taskId, {
        ...task,
        type: `PENDING`,
        isFetching: true,
      });
      return poll(subscribeForTask(task), taskId, {
        onSuccess: result => {
          _onTaskSuccess(taskId, result);
        },
        onError: result => {
          _onTaskFailure(taskId, result);
        },
        interval: task.interval,
      });
    });
  });

  return store;
};
